import { useLocalStorage } from "@/hooks/useLocalStorage";
import { useNotificationSettings } from "@/hooks/useNotificationSettings";

export interface UserPreferences {
  achievementSoundEnabled: boolean;
  achievementAnimationsEnabled: boolean;
}

export function useUserPreferences() {
  const { config } = useNotificationSettings();
  
  // User preferences stored locally, defaulting to admin settings
  const [soundEnabled, setSoundEnabled] = useLocalStorage<boolean | null>(
    "achievement-sound-enabled",
    null // null means "use admin default"
  );
  
  const [animationsEnabled, setAnimationsEnabled] = useLocalStorage<boolean | null>(
    "achievement-animations-enabled",
    null // null means "use admin default"
  );

  // Computed values: user preference ?? admin default
  const effectiveSoundEnabled = soundEnabled ?? config.soundEnabledDefault;
  const effectiveAnimationsEnabled = animationsEnabled ?? config.animationEnabled;

  const toggleSound = () => {
    setSoundEnabled(!effectiveSoundEnabled);
  };

  const toggleAnimations = () => {
    setAnimationsEnabled(!effectiveAnimationsEnabled);
  };

  const resetToDefaults = () => {
    setSoundEnabled(null);
    setAnimationsEnabled(null);
  };

  return {
    soundEnabled: effectiveSoundEnabled,
    animationsEnabled: effectiveAnimationsEnabled,
    // Raw values (null = using default)
    soundEnabledRaw: soundEnabled,
    animationsEnabledRaw: animationsEnabled,
    // Setters
    setSoundEnabled,
    setAnimationsEnabled,
    toggleSound,
    toggleAnimations,
    resetToDefaults,
    // Admin defaults for reference
    adminDefaults: {
      sound: config.soundEnabledDefault,
      animations: config.animationEnabled,
    },
  };
}
